import { useQueryClient } from "@tanstack/react-query";
import { AnimatePresence, motion } from "motion/react";

import { useTranslation } from "react-i18next";

import IconBookmarkSimple from "@/components/icons/IconBookmarkSimple";

import {
  FEED_KEYS,
  useCreateFavoriteContent,
  useDeleteFavoriteContent,
  useGetEpisodeHistory,
} from "@/hooks/api/feed.hooks";
import { useLang } from "@/hooks/useLang";
import { cn } from "@/utils/cn";
import { onError } from "@/utils/onError";
import { onSuccess } from "@/utils/onSuccess";

const DetailFavoriteButton = ({ id, className }: { id: number; className?: string }) => {
  const { mutate: createFavorite, isPending: isCreatePending } = useCreateFavoriteContent();
  const { mutate: deleteFavorite, isPending: isDeletePending } = useDeleteFavoriteContent();
  const { t } = useTranslation();

  const queryClient = useQueryClient();

  const lang = useLang();

  const { data: history, isPending } = useGetEpisodeHistory(id);

  const isLoading = isPending || isCreatePending || isDeletePending;

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: FEED_KEYS.contentDetailHistory(lang, id) });
    await queryClient.invalidateQueries({ queryKey: FEED_KEYS.favorites(lang, 48) });
  };

  const updateFavorite = () => {
    if (history?.is_favorite) {
      deleteFavorite(id, {
        onSuccess: async () => {
          await invalidate();
          onSuccess(t("Favorite.delete-success"));
        },
        onError,
      });
      return;
    }

    createFavorite(id, {
      onSuccess: async () => {
        await invalidate();
        onSuccess(t("Favorite.create-success"));
      },
      onError,
    });
  };

  return (
    <AnimatePresence mode="wait">
      {!isPending && (
        <motion.button
          type={"button"}
          exit={{ scale: 0 }}
          animate={{ scale: 1 }}
          initial={{ scale: 0 }}
          disabled={isLoading}
          onClick={updateFavorite}
          title={t("bookmarks-add")}
          key={history?.is_favorite.toString()}
          className={cn(
            "flex size-7 items-center justify-center rounded-full bg-surface-container-high px-[0.4rem] hover:bg-white-opacity-2",
            className,
          )}
        >
          <IconBookmarkSimple
            className={cn("fill-none stroke-white", {
              "fill-white": history?.is_favorite,
              "animate-pulse": isLoading,
            })}
          />
        </motion.button>
      )}
    </AnimatePresence>
  );
};

export default DetailFavoriteButton;
